import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {AttributeGroupService} from '@emm/core/services/attribute-group.service';
import {finalize, switchMap, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AttributeType} from '@emm/core/models/attribute-type.model';
import {RegexConstants} from '@emm/core/util/regex.constants';
import {Tag} from '@emm/core/models/tag.model';
import {AttributesFacadeService} from '@emm/management/services/attributes-facade.service';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {TagService} from '@emm/core/services/tag.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MatOption} from '@angular/material/core';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    selector: 'emm-management-attributes-creation-form',
    templateUrl: './management-attributes-creation-form.component.html',
    styleUrls: ['./management-attributes-creation-form.component.scss']
})
export class ManagementAttributesCreationFormComponent implements OnInit {
    @ViewChild('allSelected') allSelected: MatOption;
    attributeGroups: AttributeGroup[];
    attributeType: string;
    attributeTypes: AttributeType[];
    defaultRequiredTypes = [
        {tag: '', value: ''},
        {tag: 'true', value: 'True'},
        {tag: 'false', value: 'False'}
    ];
    isReadOnly$ = this.authFacade.isReadOnly$;
    name: string;
    requiredTypes = [
        {tag: 'true', value: 'True'},
        {tag: 'false', value: 'False'}
    ];
    scopes = [
        {tag: 'GLOBAL', value: 'Global'},
        {tag: 'LOCAL', value: 'Dynamic'}
    ];
    itemTypes = [];

    spaceValidatorPattern = RegexConstants.STARTING_SPACE_VALIDATIOR_PATTERN;
    tags: Tag[];
    disableSave;
    form: FormGroup;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    createdAttribute;
    optionsToolTip = 'The following special characters are allowed: ! - _ &';
    specialCharactersFound = false;

    constructor(
        private attributesFacade: AttributesFacadeService,
        public attributeGroupService: AttributeGroupService,
        private authFacade: AuthFacadeService,
        public tagService: TagService,
        private notificationService: NotificationService) {
    }


    ngOnInit(): void {
        this.attributesFacade.getAllItemTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            types => {
                types.forEach(type => this.itemTypes.push({tag: type.tag, value: type.name}));
            }
        );
        this.buildForm();
        this.attributesFacade.attributeTypes$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => this.attributeTypes = result);
        this.attributeGroupService.getAttributeGroups()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((attributeGroups) => {
                this.attributeGroups = attributeGroups;
            });
    }


    onNoClick(): void {
        this.clearForm();
        this.attributesFacade.nextAddClicked(false);
    }


    buildForm(): void {
        this.form = new FormGroup({
            attributeGroup: new FormControl('', Validators.required),
            defaultValue: new FormControl(''),
            description: new FormControl(''),
            fromDate: new FormControl(''),
            localeSupport: new FormControl(false),
            locked: new FormControl(false),
            objectName: new FormControl(''),
            name: new FormControl('', Validators.required),
            required: new FormControl(false, Validators.required),
            scope: new FormControl('', Validators.required),
            itemTypeScope: new FormControl(''),
            sortWeight: new FormControl(''),
            tag: new FormControl('', Validators.required),
            toDate: new FormControl(''),
            typetag: new FormControl('', Validators.required),
            options: new FormControl(''),
            validationExpression: new FormControl(''),
        });
        this.form.get('typetag').valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.form.get('defaultValue').reset());
    }

    private clearForm(): void {
        // Clear validators for each form control
        Object.keys(this.form.controls).forEach(controlName => {
            const control = this.form.get(controlName);
            control.clearValidators();
            control.updateValueAndValidity();
        });

        // After clearing validators, reset the form
        this.form.reset();
    }


    findTagExists(): void {
        if (this.form.controls['tag'].valid) {
            this.tagService.getAttributeTags(this.form.get('tag').value)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(res => {
                    if (res === true) {
                        this.form.controls['tag'].setErrors({tagExists: true});
                    }
                });
        }
    }

    OnSubmit(): void {
        this.disableSave = true;
        this.notificationService.success('Processing, please wait.')
        if (this.form.valid) {
            this.createAttribute().then(() => {});
        }
    }

    getOptions() {
        const attributeType = this.form.controls['typetag'].value;
        const optionsString = this.form.controls['options'].value;
        const regex = /[^a-zA-Z0-9_&!\-\s]/g;
        let options = [];
        if (attributeType === 'SELECT' || attributeType === 'MULTI_SELECT') {
            options = optionsString.replace(regex, ',').split(',');
            options.forEach((option, index) => options[index] = option.trim());
        }
        return options;
    }

    async createAttribute() {
        if (this.form.controls['itemTypeScope'].value === '') {
            this.form.controls['itemTypeScope'].patchValue([]);
        }

        const attribute = {
            attributeGroup: this.form.controls['attributeGroup'].value,
            defaultValue: '',
            description: this.form.controls['description'].value,
            jsonProperty: this.form.controls['objectName'].value,
            localeSupport: this.form.controls['localeSupport'].value,
            locked: this.form.controls['locked'].value,
            name: this.form.controls['name'].value,
            options: this.getOptions(),
            required: this.form.controls['required'].value,
            scope: this.form.controls['scope'].value,
            itemTypeScope: this.allSelected?.selected ? [] : this.form.controls['itemTypeScope'].value,
            sortWeight: this.form.controls['sortWeight'].value,
            tag: this.form.controls['tag'].value,
            typetag: this.form.controls['typetag'].value,
        };

        attribute.options.length > 0 ? attribute['defaultValue'] = attribute.options[0] :
            attribute['defaultValue'] = this.form.controls['defaultValue'].value;
        attribute['validationExpression'] = this.form.controls['validationExpression'].value;
        this.form.controls['itemTypeScope'].value.includes(0) ? attribute['itemTypeScope'] = [] :
            attribute['itemTypeScope'] = this.form.controls['itemTypeScope'].value;

        const typeTag = this.form.get('typetag').value;
        if (typeTag === 'DATE' && this.form.controls['date']) {
            attribute['defaultValue'] = new Date(this.form.controls['date'].value)
                .toISOString()
                .slice(0, 10);
        }

        if (attribute.typetag === 'BOOL') {
            attribute.options = ['true', 'false'];
        }

        if (attribute) {
            this.createdAttribute = attribute;
            this.attributesFacade.startProgressBar();
           await this.attributesFacade.api.createAttribute(attribute)
                .pipe(
                    finalize(() => this.attributesFacade.stopProgressBar()),
                    catchError((error) => {
                        this.clearForm();
                        this.notificationService.error(error.error);
                        // Rethrow the error to continue the error propagation
                        return throwError(() => error);
                    }),
                    tap(() => this.notificationService.success('Created successfully')),
                    switchMap(() => this.attributesFacade.api.getAllAttributes()
                        .pipe(
                            finalize(() => {
                                this.attributesFacade.stopProgressBar();
                                this.clearForm();
                            }),
                            tap((attributes) => this.attributesFacade.attributes$.next(attributes)),
                        )),
                )
                .subscribe(
                    {
                        error: (error) => {
                            this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                        }
                    }
                );
        }
    }

    numberValidation(): void {
        if (this.form.controls['typetag'].value === 'NUM' && this.form.controls['defaultValue'].value) {
            const pattern = new RegExp('^[0-9]+(.[0-9]{1,2})?$');
            if (pattern.test(this.form.controls['defaultValue'].value)) {
                this.form.controls['defaultValue'].setErrors(null);
            } else {
                this.form.controls['defaultValue'].setErrors({pattern: true});
            }
        }
    }

    validationExpressionValidation(): void {
        const validationExpression = this.form.controls['validationExpression'].value;
        const defaultValueControl = this.form.controls['defaultValue'];
        const isMatch = new RegExp(validationExpression).test(defaultValueControl.value);
        if (!validationExpression || !defaultValueControl.value || isMatch) {
            defaultValueControl.setErrors(null);
            return;
        }
        defaultValueControl.setErrors({pattern: true});
    }

    imageValidation(): void {
        const defaultValueControl = this.form.controls['defaultValue'];
        const pattern = new RegExp(
            '^https?:\\/\\/' +                              // Match http:// or https://
            '[-a-zA-Z0-9@:%._\\+~#=]{2,256}' +              // Strict domain segment
            '\\.[a-z]{2,6}\\b' +                            // Top-level domain (e.g., .com, .net)
            '([-a-zA-Z0-9@:%_\\+.~#?&/=]*)' +               // Optional path
            '\\.(webp|png|jpg|jpeg|gif)' +                      // File extension check
            '(\\?.*)?(#.*)?$',                              // Optional query and fragment
            'i'                                             // Case-insensitive
        );

        if (!defaultValueControl.value || pattern.test(defaultValueControl.value)) {
            defaultValueControl.setErrors(null);
        } else {
            defaultValueControl.setErrors({pattern: true});
        }
    }

    dateRangeUpdated($event): void {
        if ($event.target.value === '') {
            this.form.get('defaultValue').setValue(null);
        }
    }

    toggleSelectAll() {
        if (this.allSelected.selected) {
            this.form.controls['itemTypeScope']
                .patchValue([...this.itemTypes.map(type => type.tag), 0]);
        } else {
            this.form.controls['itemTypeScope'].patchValue(null);
        }
    }

    toggleSelectOne(allSelected: any) {
        if (this.allSelected.selected) {
            this.allSelected.deselect();
            return false;
        }
        if (this.form.controls['itemTypeScope'].value.length === this.itemTypes.length)
            this.allSelected.select();
    }

    optionsValidation() {
        const pattern = new RegExp( /[`~@#$%^*(){}\[\]+=\\\/|:;'".<>?]/);
        if (pattern.test(this.form.controls['options'].value)) {
            this.specialCharactersFound = true;
            return this.form.controls['options'].setErrors({pattern: true, message: ''});
        } else {
            this.specialCharactersFound = false;
            return this.form.controls['options'].setErrors(null);
        }
    }
}
